import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Music } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

function Equalizer() {
  return (
    <div className="flex items-end gap-0.5 h-5" aria-hidden data-testid="trailer-equalizer">
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className="w-1 bg-white/85 rounded-sm trailer-eq" style={{ animationDelay: `${i * 0.12}s`, height: "40%" }} />
      ))}
    </div>
  );
}

// Auto-composed "trailer": a ken-burns pan over the game's cover art + a fading snippet of
// its AI-composed soundtrack. Zero server rendering — it plays existing assets at view time.
export function GameTrailer({ image, bgmUrl, title, subtitle, emoji = "🎮", className = "", duration = 8, testid = "game-trailer" }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const startRef = useRef(0);

  const stop = () => {
    setPlaying(false);
    setProgress(0);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (audioRef.current) { try { audioRef.current.pause(); } catch (e) {} audioRef.current = null; }
  };
  useEffect(() => stop, []); // cleanup on unmount

  const toggle = () => {
    if (playing) { stop(); return; }
    if (!bgmUrl) return;
    const a = new Audio(abs(bgmUrl));
    a.volume = 0;
    a.crossOrigin = "anonymous";
    audioRef.current = a;
    startRef.current = performance.now();
    a.play().then(() => {
      setPlaying(true);
      const tick = () => {
        const el = performance.now() - startRef.current;
        const t = Math.min(1, el / (duration * 1000));
        setProgress(t);
        const fadeIn = Math.min(1, el / 500);
        const fadeOut = Math.min(1, Math.max(0, (duration * 1000 - el) / 1000));
        if (audioRef.current) audioRef.current.volume = 0.5 * fadeIn * fadeOut;
        if (t >= 1) { stop(); return; }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }).catch(() => stop());
  };

  return (
    <div className={`relative rounded-2xl overflow-hidden border border-white/10 bg-black group ${className}`} data-testid={testid} style={{ aspectRatio: "1200/630" }}>
      {image ? (
        <img src={image} alt={title || "trailer"} loading="eager"
          className={`w-full h-full object-cover ${playing ? "trailer-kenburns-active" : "trailer-kenburns"}`} data-testid="trailer-cover" />
      ) : (
        <div className="w-full h-full grid place-items-center text-7xl bg-gradient-to-br from-[#1f2440] to-[#0a0e1a]">{emoji}</div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent pointer-events-none" />

      {bgmUrl && (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-black/55 text-white/90 border border-white/15" data-testid="trailer-badge">
          <Music className="w-3 h-3" /> {playing ? "Playing" : "Trailer"}
        </span>
      )}

      <button type="button" onClick={toggle} disabled={!bgmUrl} data-testid="trailer-play-btn"
        className={`absolute inset-0 grid place-items-center ${bgmUrl ? "cursor-pointer" : "cursor-default"}`} aria-label={playing ? "Pause trailer" : "Play trailer"}>
        {bgmUrl && (
          <span className="w-16 h-16 rounded-full grid place-items-center bg-white/15 backdrop-blur border border-white/30 text-white transition-transform duration-300 group-hover:scale-110 shadow-2xl">
            {playing ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 translate-x-0.5" />}
          </span>
        )}
      </button>

      <div className="absolute left-4 right-4 bottom-4 flex items-end justify-between gap-3 pointer-events-none">
        <div className="min-w-0">
          {title && <div className="text-white font-extrabold text-lg sm:text-xl truncate drop-shadow" data-testid="trailer-title">{emoji} {title}</div>}
          {subtitle && <div className="text-white/70 text-xs truncate">{subtitle}</div>}
        </div>
        {playing && <Equalizer />}
      </div>

      <div className="absolute left-0 right-0 bottom-0 h-1 bg-white/10">
        <div className="h-full bg-gradient-to-r from-[#7c3aed] to-[#3b9eff]" style={{ width: `${progress * 100}%`, transition: "width 100ms linear" }} data-testid="trailer-progress" />
      </div>
    </div>
  );
}

export default GameTrailer;
